import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { Ruler3D } from '../decor/Ruler3D'
import { TankDecorGroup3D } from '../decor/TankDecorGroup3D'
import { FeatureDispatch } from '../features/FeatureDispatch'
import { cartesianCircleGeometry as geometry } from '@asset-viz/utils/geometryDefines'
import { extractScale, Vec3 } from '@asset-viz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import {
  GeckoAssetModel,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoSphereFlatCap,
} from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A surface representing a circular sphere flat cap, plotted on a cartesian plane,
 * with the center of its base at "position" and decorated with Rich Asset
 * features specific to flat caps.
 * @param model               The sphere flat cap component model
 * @param assetModel          The asset model for the asset that the cap is a part of`
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */

export const SphereFlatCapSurface3D: React.FC<
  {
    model: GeckoSphereFlatCap
    assetModel: GeckoAssetModel
  } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const {
    bounds,
    scale,
    manways,
    nozzles,
    verticalWelds,
    horizontalWelds,
    plates,
    polygonalPlates,
  } = React.useMemo(
    () => ({
      bounds: model.componentBounds,
      scale: extractScale(groupProps.scale).multiply(
        new Vector3(
          model.radius.scale(2.0).num,
          model.radius.scale(2.0).num,
          1.0
        )
      ),
      manways: model.manwaysArray,
      nozzles: model.nozzlesArray,
      verticalWelds: model.verticalWeldsArray,
      horizontalWelds: model.horizontalWeldsArray,
      plates: model.platesArray,
      polygonalPlates: model.polygonalPlatesArray,
    }),
    [model, groupProps.scale]
  )

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      // Center-based coordinates: normalize to -0.5 to 0.5 range for rendering
      const relX =
        coord
          .convertTo(GeckoMeasurementUnit.Meters)
          .minus(model.componentBounds.start.x)
          .scale(1.0 / model.componentBounds.width.num).num - 0.5
      return [relX, -0.6, 0] // Y offset for ruler position
    },
    [model.componentBounds]
  )

  const positionYTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      // Center-based coordinates: normalize to -0.5 to 0.5 range for rendering
      const relY =
        coord
          .convertTo(GeckoMeasurementUnit.Meters)
          .minus(model.componentBounds.start.y)
          .scale(1.0 / model.componentBounds.height.num).num - 0.5
      return [-0.6, relY, 0] // X offset for ruler position
    },
    [model.componentBounds]
  )

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      // Apply the true scaling last (override anything in groupProps)
      scale={scale}>
      <TankDecorGroup3D yPivot={show3D}>
        <Ruler3D
          anchor={'right'}
          start={model.componentBounds.start.y}
          end={model.componentBounds.end.y}
          includeTicks
          includeStart
          includeEnd
          getTickPosition={positionYTick}
        />
      </TankDecorGroup3D>
      <Ruler3D
        anchor={'top'}
        start={model.componentBounds.start.x}
        end={model.componentBounds.end.x}
        includeTicks
        includeStart
        includeEnd
        getTickPosition={positionXTick}
      />

      <FeatureDispatch
        features={[
          manways,
          nozzles,
          verticalWelds,
          horizontalWelds,
          plates,
          polygonalPlates,
        ]}
      />
    </ModelSurface3D>
  )
}
